"use client";

import React from 'react';
import { motion } from 'framer-motion';
import { useComponentTranslations } from '@/app/components/LanguageProvider';
import { Icon } from '@iconify/react';
import ServicesAccordion from '@/app/components/ServicesAccordion';
import AlternatingUseCases from '@/app/components/AlternatingUseCases';
import TechSection from '@/components/industry/TechSection';
import { IndustrialsForm } from '@/app/components/IndustrialsForm';

const UtilitiesClient = () => {
    const { t, translationsLoaded } = useComponentTranslations('Utilities');
    const ut = (key: string, fallback?: string) => {
        if (!translationsLoaded) return fallback ?? key;
        return t(key, fallback) ?? fallback ?? key;
    };

    const expertiseItems = [
        { title: ut('utilitiesExpertise1'), icon: 'carbon:analytics' },
        { title: ut('utilitiesExpertise2'), icon: 'carbon:machine-learning-model' },
        { title: ut('utilitiesExpertise3'), icon: 'carbon:rule' },
        { title: ut('utilitiesExpertise4'), icon: 'carbon:security' },
        { title: ut('utilitiesExpertise5'), icon: 'carbon:cloud-app' },
        { title: ut('utilitiesExpertise6'), icon: 'carbon:blockchain' },
        { title: ut('utilitiesExpertise7'), icon: 'carbon:data-refinery' },
        { title: ut('utilitiesExpertise8'), icon: 'carbon:chart-line-data' },
        { title: ut('utilitiesExpertise9'), icon: 'carbon:user-profile' },
    ];

    const useCasesForAccordion = [
        { title: ut('utilitiesUseCase1'), description: ut('utilitiesUseCase1Desc'), backgroundImage: '/images/robot-with-laptop-computer-mouse_1048-3560.jpg' },
        { title: ut('utilitiesUseCase2'), description: ut('utilitiesUseCase2Desc'), backgroundImage: '/images/robot-with-shopping-cart_1048-3574.jpg' },
        { title: ut('utilitiesUseCase3'), description: ut('utilitiesUseCase3Desc'), backgroundImage: '/images/robot-with-clipboard-boxes_1048-3603.jpg' },
        { title: ut('utilitiesUseCase4'), description: ut('utilitiesUseCase4Desc'), backgroundImage: '/images/education_1.jpg' },
    ];

    const servicesForAlternating = [
        { title: ut('utilitiesService1Title'), description: ut('utilitiesService1Desc'), image: '/images/finance.jpg' },
        { title: ut('utilitiesService2Title'), description: ut('utilitiesService2Desc'), image: '/images/digital-marketing.jpg' },
        { title: ut('utilitiesService3Title'), description: ut('utilitiesService3Desc'), image: '/images/robot-constructor_1048-4570.jpg' },
        { title: ut('utilitiesService4Title'), description: ut('utilitiesService4Desc'), image: '/images/robot-with-clipboard_1048-3605.jpg' },
        { title: ut('utilitiesService5Title'), description: ut('utilitiesService5Desc'), image: '/images/robot-working-with-computer_1048-4282.jpg' },
    ];

    const faqItems = [
        { question: ut('utilitiesFaq1q'), answer: ut('utilitiesFaq1a') },
        { question: ut('utilitiesFaq2q'), answer: ut('utilitiesFaq2a') },
        { question: ut('utilitiesFaq3q'), answer: ut('utilitiesFaq3a') },
        { question: ut('utilitiesFaq4q'), answer: ut('utilitiesFaq4a') },
        { question: ut('utilitiesFaq5q'), answer: ut('utilitiesFaq5a') },
    ];

    return (
        <div className="relative bg-transparent text-white">
            <header className="flex flex-col justify-center items-center text-center px-4 sm:px-6 lg:px-8 pt-24">
                <motion.h1
                    className="text-3xl lg:text-7xl font-bold tracking-tight mb-6 bg-linear-to-b from-white to-white/90 bg-clip-text text-transparent"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{ willChange: "transform, opacity" }}
                >
                    {ut('utilitiesHeroTitle')}
                </motion.h1>
                <motion.p
                    className="text-lg text-white/70 max-w-3xl mx-auto pb-24"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    style={{ willChange: "transform, opacity" }}
                >
                    {ut('utilitiesHeroSubtitle')}
                </motion.p>
            </header>

            <section className="w-full py-24 glass-section glass-border-y">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center mb-12">{ut('utilitiesPainTitle')}</h2>
                    <p className="text-lg text-white/70 text-center max-w-4xl mx-auto">
                        {ut('utilitiesPainIntro')}
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <section className="my-24">
                    <h2 className="text-3xl font-bold text-center mb-12">{ut('utilitiesExpertiseTitle')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {expertiseItems.map((item, index) => (
                            <motion.div
                                key={index}
                                className="p-6 glass-border-strong glass-rounded glass-card flex flex-col items-center text-center"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.4, delay: index * 0.05 }}
                                style={{ willChange: "transform, opacity" }}
                            >
                                <Icon icon={item.icon} className="text-4xl mb-4 text-blue-400" />
                                <h3 className="font-semibold">{item.title}</h3>
                            </motion.div>
                        ))}
                    </div>
                </section>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <AlternatingUseCases title={ut('utilitiesServicesTitle')} useCases={servicesForAlternating} />
            </div>

            <ServicesAccordion title={ut('utilitiesUseCasesTitle')} services={useCasesForAccordion} />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <TechSection title={ut('utilitiesTechTitle')} t={t} />
                <IndustrialsForm label={ut('utilitiesCtaTitle')} />

                <section className="my-24 max-w-4xl mx-auto">
                    <h2 className="text-3xl font-bold text-center mb-12">{ut('utilitiesFaqTitle')}</h2>
                    <div className="space-y-4">
                        {faqItems.map((item, index) => (
                            <details key={index} className="p-4 glass-border-strong glass-rounded glass-card">
                                <summary className="font-semibold cursor-pointer">{item.question}</summary>
                                <p className="mt-2 text-white/70">{item.answer}</p>
                            </details>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default UtilitiesClient;
